<template>
  <div class="relative w-full">
    <div
      class="flex flex-wrap items-center gap-2 rounded-md border-0 py-1.5 pl-3 pr-3 shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:text-sm sm:leading-6"
      :class="[
        error ? 'ring-red-500 focus-within:ring-red-500' : '',
        disabled
          ? 'cursor-not-allowed bg-gray-50 text-gray-500 dark:bg-white/10 dark:text-gray-400'
          : 'bg-white/5 text-gray-900 dark:bg-white/5 dark:text-white',
      ]"
    >
      <!-- Render existing labels -->
      <span
        v-for="(label, index) in modelValue"
        :key="index"
        class="inline-flex items-center gap-x-0.5 rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10 dark:bg-indigo-400/10 dark:text-indigo-400 dark:ring-indigo-400/30"
      >
        {{ label }}
        <button
          v-if="!disabled"
          type="button"
          @click="removeLabel(index)"
          class="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-indigo-600/20"
        >
          <span class="sr-only">Remove</span>
          <svg viewBox="0 0 14 14" class="h-3.5 w-3.5 stroke-indigo-700/50 group-hover:stroke-indigo-700/75 dark:stroke-indigo-400/50 dark:group-hover:stroke-indigo-400/75">
            <path d="M4 4l6 6m0-6l-6 6" />
          </svg>
          <span class="absolute -inset-1" />
        </button>
      </span>

      <!-- Input for new labels -->
      <input
        type="text"
        v-model="inputValue"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="showDropdown"
        :aria-controls="listboxId"
        :aria-activedescendant="activeOptionId"
        autocomplete="off"
        @input="onInput"
        @focus="openDropdown"
        @keydown="onKeydown"
        @blur="onBlur"
        :placeholder="modelValue && modelValue.length === 0 ? placeholder : ''"
        class="flex-1 min-w-[120px] border-0 bg-transparent p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6 dark:text-white"
        :id="id"
        :name="name"
        :disabled="disabled"
      />
    </div>

    <!-- Suggestions from labels already used on other runners -->
    <ul
      v-if="showDropdown"
      :id="listboxId"
      role="listbox"
      class="absolute left-0 z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none dark:border-white/10 dark:bg-gray-800"
    >
      <li
        v-for="(suggestion, index) in filteredSuggestions"
        :key="suggestion"
        :id="optionId(index)"
        role="option"
        :aria-selected="index === activeIndex"
        @mousedown.prevent="selectSuggestion(suggestion)"
        @mouseenter="activeIndex = index"
        class="cursor-pointer select-none px-3 py-2 transition-colors"
        :class="
          index === activeIndex
            ? 'bg-indigo-600 text-white dark:bg-indigo-500'
            : 'text-gray-700 dark:text-gray-300'
        "
      >
        {{ suggestion }}
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import {computed, ref, useId, watch} from 'vue';

const props = defineProps<{
  modelValue: string[];
  suggestions?: string[];
  placeholder?: string;
  id?: string;
  name?: string;
  error?: boolean;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void;
}>();

const inputValue = ref('');
const isOpen = ref(false);
const activeIndex = ref(-1);

const fallbackId = useId();
const baseId = computed(() => props.id ?? `labels-input-${fallbackId}`);
const listboxId = computed(() => `${baseId.value}-suggestions`);

// Suggestions not yet selected, filtered by the current input.
// Matching is case-insensitive, but the original casing is preserved.
const filteredSuggestions = computed(() => {
  const query = inputValue.value.trim().toLowerCase();
  return (props.suggestions ?? []).filter((suggestion) => {
    if (props.modelValue.includes(suggestion)) return false;
    return !query || suggestion.toLowerCase().includes(query);
  });
});

const showDropdown = computed(() => isOpen.value && filteredSuggestions.value.length > 0);

const activeOptionId = computed(() =>
  showDropdown.value && activeIndex.value >= 0 ? optionId(activeIndex.value) : undefined
);

function optionId(index: number) {
  return `${baseId.value}-option-${index}`;
}

function openDropdown() {
  isOpen.value = true;
  activeIndex.value = -1;
}

function closeDropdown() {
  isOpen.value = false;
  activeIndex.value = -1;
}

function addLabel() {
  const val = inputValue.value.trim();
  if (val) {
    // Only add if it's not a duplicate
    if (!props.modelValue.includes(val)) {
      emit('update:modelValue', [...props.modelValue, val]);
    }
  }
  inputValue.value = '';
  activeIndex.value = -1;
}

function selectSuggestion(suggestion: string) {
  if (!props.modelValue.includes(suggestion)) {
    emit('update:modelValue', [...props.modelValue, suggestion]);
  }
  inputValue.value = '';
  activeIndex.value = -1;
  // Keep the dropdown open (input retains focus) so more labels can be picked.
}

function removeLabel(index: number) {
  const newLabels = [...props.modelValue];
  newLabels.splice(index, 1);
  emit('update:modelValue', newLabels);
}

// User typing re-opens the dropdown and resets the highlight. Programmatic
// clears (e.g. after adding a label) do not trigger this DOM event handler.
function onInput() {
  isOpen.value = true;
  activeIndex.value = -1;
}

function onKeydown(e: KeyboardEvent) {
  switch (e.key) {
    case 'Enter':
      e.preventDefault();
      if (showDropdown.value && activeIndex.value >= 0) {
        selectSuggestion(filteredSuggestions.value[activeIndex.value]);
      } else {
        addLabel();
      }
      break;
    case ',':
      // Comma is a label separator (labels are stored comma-separated).
      e.preventDefault();
      addLabel();
      break;
    case 'Tab':
      if (showDropdown.value && activeIndex.value >= 0) {
        e.preventDefault();
        selectSuggestion(filteredSuggestions.value[activeIndex.value]);
      } else if (inputValue.value.trim()) {
        e.preventDefault();
        addLabel();
      }
      // Otherwise allow normal Tab navigation; blur closes the dropdown.
      break;
    case 'ArrowDown':
      e.preventDefault();
      // Re-open if closed (e.g. after Escape) without resetting the highlight
      isOpen.value = true;
      if (filteredSuggestions.value.length > 0) {
        activeIndex.value = (activeIndex.value + 1) % filteredSuggestions.value.length;
      }
      break;
    case 'ArrowUp':
      e.preventDefault();
      isOpen.value = true;
      if (filteredSuggestions.value.length > 0) {
        activeIndex.value =
          activeIndex.value <= 0 ? filteredSuggestions.value.length - 1 : activeIndex.value - 1;
      }
      break;
    case 'Escape':
      if (showDropdown.value) {
        e.preventDefault();
        closeDropdown();
      }
      break;
  }
}

function onBlur() {
  addLabel();
  closeDropdown();
}

// Keep the highlighted option visible while navigating with the keyboard.
watch(activeIndex, (index) => {
  if (index < 0) return;
  document.getElementById(optionId(index))?.scrollIntoView?.({block: 'nearest'});
});
</script>
